import { PrimaryButton as PrimaryButtonBase } from 'components/misc/Buttons.js';
import { SectionHeading } from 'components/misc/Headings.js';
import { ReactComponent as BriefcaseIcon } from 'feather-icons/dist/icons/briefcase.svg';
import { ReactComponent as MoneyIcon } from 'feather-icons/dist/icons/dollar-sign.svg';
import PrototypeIllustrationSrc from 'images/prototype-illustration.svg';
import styled from 'styled-components';
import { css } from 'styled-components/macro'; //eslint-disable-line
import tw from 'twin.macro';

const PrimaryText = tw.span`text-primary-500`;
const Subheading = tw.span`text-center md:text-left uppercase tracking-widest font-bold text-primary-500`;

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-6 md:py-8`;
const Column = tw.div`w-full max-w-md md:max-w-none`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)((props) => [
    tw`md:w-7/12 mt-16 md:mt-0`,
    props.textOnLeft ? tw`md:mr-8 lg:mr-12 md:order-first` : tw`md:ml-8 lg:ml-12 md:order-last`,
]);

const Image = styled.div((props) => [
    `background-image: url("${props.imageSrc}");`,
    tw`rounded bg-contain bg-no-repeat bg-center h-1/2`,
]);
const TextContent = tw.div`lg:py-0 text-center md:text-left`;
const Heading = tw(
    SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-base md:text-base lg:text-lg font-medium leading-relaxed text-secondary-400`;

const Features = styled.div((props) => [tw`max-w-xl mx-auto md:mx-0`, props.hideTitle ? tw`mt-4` : tw`mt-4 md:mt-8`]);
const Feature = styled.div((props) => [
    tw`flex items-start md:flex-row`,
    props.hideTitle ? tw`mt-2` : tw`mt-4 md:mt-8`,
]);

const FeatureIconContainer = styled.div((props) => [
    tw`mx-auto md:mx-1 inline-block border border-primary-500 text-center rounded-full flex-shrink-0`,
    props.hideTitle ? tw`p-1` : tw`p-2`,
    props.hideTitle
        ? css`
              svg {
                  ${tw`w-4 h-4 text-primary-500`}
              }
          `
        : css`
              svg {
                  ${tw`w-5 h-5 text-primary-500`}
              }
          `,
]);

const FeatureText = tw.div`w-full ml-4 text-left`;
const FeatureHeading = tw.div`font-bold text-lg text-primary-500`;
const FeatureDescription = styled.div((props) => [tw`text-sm`, props.hideTitle ? tw`mt-0 md:mt-1` : tw`mt-0 md:mt-1`]);

const PrimaryButton = tw(PrimaryButtonBase)`mt-8 md:mt-10 md:mx-0`;

export default ({
    subheading = 'Our Expertise',
    heading = (
        <>
            We have the most <PrimaryText>professional</PrimaryText> marketing team.
        </>
    ),
    description = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    description2 = '',
    primaryButtonText = 'Kontaktirajte nas',
    primaryButtonUrl = '#kontakt',
    features = null,
    textOnLeft = true,
    imageSrc = PrototypeIllustrationSrc,
    image2Src = null,
    features2 = null,
    description3 = null,
    hideTitle = false,
}) => {
    // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.

    /*
     * Change the features variable as you like, add or delete objects
     * `icon` must be a React SVG component. See how BriefcaseIcon is imported above. For a full list of available icons, see Feather Icons.
     */
    const defaultFeatures = [
        {
            Icon: BriefcaseIcon,
            title: 'Professionalism',
            description: 'We have the best professional marketing people across the globe just to work with you.',
        },
        {
            Icon: MoneyIcon,
            title: 'Affordable',
            description: 'We promise to offer you the best rate we can - at par with the industry standard.',
        },
    ];

    if (!features) features = defaultFeatures;

    return (
        <Container>
            <TwoColumn>
                <ImageColumn>
                    <Image imageSrc={imageSrc} />
                    {image2Src && <Image imageSrc={image2Src} />}
                </ImageColumn>
                <TextColumn textOnLeft={textOnLeft}>
                    <TextContent>
                        <Subheading>{subheading}</Subheading>
                        <Heading>{heading}</Heading>
                        <Description>{description}</Description>
                        <Features hideTitle={hideTitle}>
                            {features.map((feature, index) => (
                                <Feature key={`features1-${index}`} hideTitle={hideTitle}>
                                    <FeatureIconContainer hideTitle={hideTitle}>
                                        {<feature.Icon />}
                                    </FeatureIconContainer>
                                    <FeatureText>
                                        {!hideTitle && <FeatureHeading>{feature.title}</FeatureHeading>}
                                        <FeatureDescription hideTitle={hideTitle}>
                                            {feature.description}
                                        </FeatureDescription>
                                    </FeatureText>
                                </Feature>
                            ))}
                        </Features>
                        {description2 && <Description>{description2}</Description>}
                        {features2 && (
                            <Features hideTitle={hideTitle}>
                                {features2.map((feature, index) => (
                                    <Feature key={`features2-${index}`} hideTitle={hideTitle}>
                                        <FeatureIconContainer hideTitle={hideTitle}>
                                            {<feature.Icon />}
                                        </FeatureIconContainer>
                                        <FeatureText>
                                            {!hideTitle && <FeatureHeading>{feature.title}</FeatureHeading>}
                                            <FeatureDescription hideTitle={hideTitle}>
                                                {feature.description}
                                            </FeatureDescription>
                                        </FeatureText>
                                    </Feature>
                                ))}
                            </Features>
                        )}
                        {description3 && <Description>{description3}</Description>}
                        <PrimaryButton as="a" href={primaryButtonUrl}>
                            {primaryButtonText}
                        </PrimaryButton>
                    </TextContent>
                </TextColumn>
            </TwoColumn>
        </Container>
    );
};
